import { useState, useMemo, useCallback } from 'react';
import { useCoverageData, useCoverageDrillDown } from '../../../hooks/useCoverageData';
import { useCSVExport } from '../../../hooks/useCSVExport';
import { useToast } from '@/shared/components/Toast';
import { CoverageMap } from '../provider-map/CoverageMap';
import { CoverageFilters } from '../provider-map/CoverageFilters';
import { ProviderDrillDown } from '../provider-map/ProviderDrillDown';
import { ExportButton } from '../shared/ExportButton';
import { OverviewSectionSkeleton } from './OverviewSectionSkeleton';
import { OverviewSectionError } from './OverviewSectionError';
import type {
  AnalyticsQueryParams,
  VettingStatusFilter,
} from '../../../types/analytics.types';

type NetworkCoverageSectionProps = {
  queryParams: AnalyticsQueryParams;
  isActive?: boolean;
};

export const NetworkCoverageSection = ({ queryParams, isActive = true }: NetworkCoverageSectionProps) => {
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [vettingStatus, setVettingStatus] = useState<VettingStatusFilter>('APPROVED');
  const [drillDownState, setDrillDownState] = useState<string | null>(null);
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const { showToast } = useToast();

  const handleExportError = useCallback(
    (message: string) => {
      showToast(`Export failed: ${message}. Please try again.`, 'error', 5000);
    },
    [showToast],
  );

  const csvExport = useCSVExport({ onError: handleExportError });

  const coverageParams = useMemo(
    () => ({
      ...queryParams,
      ...(categoryId ? { categoryId } : {}),
      vettingStatus,
    }),
    [queryParams, categoryId, vettingStatus],
  );

  const coverage = useCoverageData(coverageParams, isActive);

  const drillDownParams = useMemo(
    () => ({
      ...coverageParams,
      stateCode: drillDownState ?? '',
      limit: 20,
    }),
    [coverageParams, drillDownState],
  );

  const drillDown = useCoverageDrillDown(drillDownParams, drillDownOpen && !!drillDownState);

  const allDrillDownProviders = useMemo(
    () => drillDown.data?.pages.flatMap((p) => p.providers) ?? [],
    [drillDown.data?.pages],
  );

  const drillDownTotal = drillDown.data?.pages[0]?.totalCount ?? 0;

  const drillDownStateName = useMemo(() => {
    if (!drillDownState || !coverage.data) return '';
    return (
      coverage.data.states.find((s) => s.stateCode === drillDownState)?.stateName ??
      drillDownState
    );
  }, [drillDownState, coverage.data]);

  const handleStateClick = (stateCode: string) => {
    setDrillDownState(stateCode);
    setDrillDownOpen(true);
  };

  const allCategories = useMemo(() => {
    if (!coverage.data) return [];
    const catMap = new Map<string, string>();
    coverage.data.states.forEach((s) =>
      s.categories.forEach((c) => catMap.set(c.categoryId, c.categoryName)),
    );
    return Array.from(catMap, ([categoryId, categoryName]) => ({
      categoryId,
      categoryName,
    }));
  }, [coverage.data]);

  if (coverage.isLoading) return <OverviewSectionSkeleton title="Provider Coverage" variant="coverage" />;
  if (coverage.isError) {
    return <OverviewSectionError title="Provider Coverage" onRetry={() => coverage.refetch()} />;
  }
  if (!coverage.data) return null;

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Provider Coverage</h2>
        <ExportButton
          onClick={() =>
            csvExport.exportCSV('coverage', coverageParams as unknown as Record<string, string>)
          }
          isLoading={csvExport.isExporting}
        />
      </div>
      <div className="space-y-4">
        <CoverageFilters
          categories={allCategories}
          selectedCategoryId={categoryId}
          onCategoryChange={setCategoryId}
          selectedVettingStatus={vettingStatus}
          onVettingStatusChange={setVettingStatus}
        />
        <CoverageMap
          data={coverage.data.states}
          totalProviders={coverage.data.totalProviders}
          onStateClick={handleStateClick}
        />
      </div>
      <ProviderDrillDown
        open={drillDownOpen}
        onOpenChange={setDrillDownOpen}
        stateCode={drillDownState}
        stateName={drillDownStateName}
        providers={allDrillDownProviders}
        totalCount={drillDownTotal}
        hasNextPage={drillDown.hasNextPage ?? false}
        isFetchingNextPage={drillDown.isFetchingNextPage}
        isLoading={drillDown.isLoading}
        onLoadMore={() => drillDown.fetchNextPage()}
      />
    </section>
  );
};
